import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import db from '../services/db';
import { useAuth } from '../hooks/useAuth';
import SelfStudyLessonView from '../components/SelfStudyLessonView';

function isAssignmentCompleted(assignment) {
    if (!assignment) return false;
    if (assignment.status === 'completed' || assignment.status === 'checked') return true;
    if (assignment.block_id) {
        const subStatuses = assignment.sub_statuses || {};
        const ids = Object.keys(subStatuses);
        return ids.length > 0 && ids.every((subId) => {
            const status = subStatuses[subId]?.status;
            return status === 'completed' || status === 'checked';
        });
    }
    return false;
}

function isBlockAssignmentCompleted(assignment) {
    if (!assignment) return false;
    if (assignment.status === 'completed' || assignment.status === 'checked') return true;
    const subStatuses = assignment.sub_statuses || {};
    const subIds = Object.keys(subStatuses);
    return subIds.length > 0 && subIds.every((subId) => {
        const status = subStatuses[subId]?.status;
        return status === 'completed' || status === 'checked';
    });
}

export default function SelfStudyLessonPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const groupId = searchParams.get('groupId');
    const { user } = useAuth();
    const [lesson, setLesson] = useState(null);
    const [exercises, setExercises] = useState([]);
    const [blocks, setBlocks] = useState([]);
    const [fullLessonSections, setFullLessonSections] = useState([]);
    const [assignments, setAssignments] = useState([]);
    const [initialCompletedSections, setInitialCompletedSections] = useState({});
    const [lessonProgress, setLessonProgress] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isCompleting, setIsCompleting] = useState(false);

    useEffect(() => {
        loadLessonData();
    }, [id, user?.id, groupId]);

    const loadLessonData = async () => {
        try {
            setLoading(true);
            const [lessons, exData, bData, assignmentRows, lessonProgressRows, sectionRows] = await Promise.all([
                db.getSyllabusLessons ? db.getSyllabusLessons() : Promise.resolve([]),
                db.getExercises(),
                db.getBlocks(),
                (user?.id && db.getAssignmentsForStudent)
                    ? db.getAssignmentsForStudent(user.id)
                    : Promise.resolve([]),
                (user?.id && groupId && db.getSelfStudyLessonProgress)
                    ? db.getSelfStudyLessonProgress(user.id, groupId)
                    : Promise.resolve([]),
                db.getFullLessonSections ? db.getFullLessonSections(id) : Promise.resolve([]),
            ]);

            const targetLesson = lessons.find((l) => l.id === id);
            setLesson(targetLesson || null);
            setAssignments(Array.isArray(assignmentRows) ? assignmentRows : []);
            setExercises(exData || []);
            setBlocks(bData || []);
            setFullLessonSections(Array.isArray(sectionRows) ? sectionRows : []);
            setLessonProgress((Array.isArray(lessonProgressRows) ? lessonProgressRows : []).find((p) => p.lesson_id === id) || null);

            if (targetLesson) {
                const assignmentList = (Array.isArray(assignmentRows) ? assignmentRows : []).filter(
                    (a) => !a.group_id || String(a.group_id) === String(groupId)
                );
                const getMatchingAssignment = (item) => assignmentList.find((a) => {
                    if (item.content_type === 'exercise') return String(a.exercise_id || '') === String(item.content_id);
                    if (item.content_type === 'block') return String(a.block_id || '') === String(item.content_id);
                    return false;
                });

                const allSectionItems = (Array.isArray(sectionRows) ? sectionRows : []).flatMap((section) =>
                    (Array.isArray(section.items) ? section.items : []).map((item) => ({ ...item, section_id: section.id }))
                );

                const missingAssignments = allSectionItems.filter((item) => !getMatchingAssignment(item));
                if (missingAssignments.length > 0 && user?.id && groupId) {
                    for (const item of missingAssignments) {
                        try {
                            if (item.content_type === 'exercise') {
                                const ex = (exData || []).find((e) => String(e.id) === String(item.content_id));
                                if (ex) {
                                    const newAssignment = await db.assignExercise({
                                        exercise_id: ex.id,
                                        student_id: user.id,
                                        group_id: groupId,
                                        status: 'assigned',
                                    });
                                    assignmentList.push(newAssignment);
                                }
                            } else if (item.content_type === 'block') {
                                const block = (bData || []).find((b) => String(b.id) === String(item.content_id));
                                if (block) {
                                    const newAssignment = await db.assignExercise({
                                        block_id: block.id,
                                        student_id: user.id,
                                        group_id: groupId,
                                        status: 'assigned',
                                    });
                                    assignmentList.push(newAssignment);
                                }
                            }
                        } catch (e) {
                            console.error('[SelfStudyLessonPage] Failed to auto-assign item:', item, e);
                        }
                    }
                    setAssignments([...assignmentList]);
                }

                const progressCompleted = (Array.isArray(lessonProgressRows) ? lessonProgressRows : []).find((p) => p.lesson_id === id)?.is_completed;
                const initialCompleted = {};
                allSectionItems.forEach((item) => {
                    const assignment = getMatchingAssignment(item);
                    const done = progressCompleted
                        || (item.content_type === 'block'
                            ? isBlockAssignmentCompleted(assignment)
                            : isAssignmentCompleted(assignment));
                    if (done) initialCompleted[item.id] = true;
                });
                setInitialCompletedSections(initialCompleted);
            } else {
                setInitialCompletedSections({});
            }
        } catch (e) {
            console.error('Failed to load lesson data:', e);
        } finally {
            setLoading(false);
        }
    };

    const handleExerciseUpdate = async (assignment, data) => {
        if (!assignment?.id) return;
        const { status, ...progressData } = data;
        const delta = {
            ...progressData,
            updated_at: new Date().toISOString(),
        };
        await db.updateAssignment(assignment.id, delta);
        setAssignments((prev) => prev.map((a) => (a.id === assignment.id ? { ...a, ...delta } : a)));
    };

    const handleExerciseComplete = async (item, assignment, data = {}) => {
        if (!assignment?.id) return;
        const delta = {
            ...data,
            status: 'completed',
            completed_at: new Date().toISOString(),
        };
        await db.updateAssignment(assignment.id, delta);
        setAssignments((prev) => prev.map((a) => (a.id === assignment.id ? { ...a, ...delta } : a)));

        try {
            const exerciseMeta = (exercises || []).find((e) => String(e.id) === String(item.content_id));
            const assessment = exerciseMeta?.assessment || {};
            const score = data?.score || 0;
            const starsAwarded = (score * (parseFloat(assessment.starsPerGap) || 0)) + (parseFloat(assessment.starsOnComplete) || 0);
            const coinsAwarded = (score * (parseInt(assessment.coinsPerGap, 10) || 0)) + (parseInt(assessment.coinsOnComplete, 10) || 0);
            await db.logStudentEvent({
                user_id: user.id,
                type: 'exercise_complete_submitted',
                path: '/self-study',
                payload: {
                    assignmentId: assignment.id,
                    exerciseId: exerciseMeta?.id || item?.content_id,
                    title: exerciseMeta?.title || null,
                    score,
                    starsAwarded,
                    coinsAwarded,
                },
            });
        } catch (_) { /* analytics optional */ }
    };

    const handleBlockSubComplete = async (item, block, assignment, exercise, data = {}) => {
        if (!assignment?.id) return;
        const now = new Date().toISOString();
        const subStatuses = { ...(assignment.sub_statuses || {}) };
        subStatuses[exercise.id] = {
            ...data,
            status: 'completed',
            completed_at: now,
        };
        const blockExerciseIds = Array.isArray(block?.exercise_ids) ? block.exercise_ids.map((eid) => String(eid)) : [];
        const allDone = blockExerciseIds.length > 0 && blockExerciseIds.every((exId) => {
            const status = subStatuses[exId]?.status;
            return status === 'completed' || status === 'checked';
        });
        const delta = {
            sub_statuses: subStatuses,
            ...(allDone ? { status: 'completed', completed_at: now } : {}),
        };
        await db.updateAssignment(assignment.id, delta);
        setAssignments((prev) => prev.map((a) => (a.id === assignment.id ? { ...a, ...delta } : a)));
    };

    const handleMarkAsComplete = async () => {
        if (!user?.id || !groupId || !lesson) return;

        try {
            setIsCompleting(true);
            await db.completeLesson(user.id, lesson.id, groupId);
            const nextLesson = await db.getNextLesson(lesson.course, lesson.module, lesson.lesson);

            if (nextLesson) {
                await db.unlockLesson(user.id, nextLesson.id, groupId);
                const [allSyllabusItems] = await Promise.all([
                    db.getSyllabusItems ? db.getSyllabusItems() : Promise.resolve([]),
                ]);
                const nextLessonItems = allSyllabusItems.filter((item) =>
                    item.course === nextLesson.course
                    && item.module === nextLesson.module
                    && item.lesson === nextLesson.lesson
                );

                const assignedExerciseIds = new Set();
                const assignedBlockIds = new Set();

                for (const item of nextLessonItems) {
                    if (item.exercise_ids && Array.isArray(item.exercise_ids)) {
                        for (const exerciseId of item.exercise_ids) {
                            if (assignedExerciseIds.has(String(exerciseId))) continue;
                            const exercise = exercises.find((e) => e.id === exerciseId);
                            if (exercise) {
                                await db.assignExercise({
                                    exercise_id: exercise.id,
                                    student_id: user.id,
                                    group_id: groupId,
                                    status: 'assigned',
                                });
                                assignedExerciseIds.add(String(exerciseId));
                            }
                        }
                    }
                    if (item.block_ids && Array.isArray(item.block_ids)) {
                        for (const blockId of item.block_ids) {
                            if (assignedBlockIds.has(String(blockId))) continue;
                            const block = blocks.find((b) => b.id === blockId);
                            if (block) {
                                await db.assignExercise({
                                    block_id: block.id,
                                    student_id: user.id,
                                    group_id: groupId,
                                    status: 'assigned',
                                });
                                assignedBlockIds.add(String(blockId));
                            }
                        }
                    }
                }

                await db.assignExercisesFromFullLessonSections(nextLesson.id, user.id, groupId, assignedExerciseIds, assignedBlockIds);
                alert(`Lesson completed! Next lesson "${nextLesson.title || `Lesson ${nextLesson.lesson}`}" has been unlocked.`);
            } else {
                alert('Lesson completed! You have finished the course!');
            }

            await loadLessonData();
            navigate(-1);
        } catch (e) {
            console.error('Failed to complete lesson:', e);
            alert('Failed to complete lesson. Please try again.');
            throw e;
        } finally {
            setIsCompleting(false);
        }
    };

    if (loading) {
        return (
            <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>
                Loading lesson...
            </div>
        );
    }

    return (
        <SelfStudyLessonView
            lesson={lesson}
            exercises={exercises}
            blocks={blocks}
            fullLessonSections={fullLessonSections}
            user={user}
            assignments={assignments}
            groupId={groupId}
            lessonProgress={lessonProgress}
            initialCompletedSections={initialCompletedSections}
            onExit={() => navigate(-1)}
            onFinishLesson={handleMarkAsComplete}
            isCompleting={isCompleting}
            onExerciseUpdate={handleExerciseUpdate}
            onExerciseComplete={handleExerciseComplete}
            onBlockSubComplete={handleBlockSubComplete}
        />
    );
}
